import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom"; 
import { useSessionResponses } from "../../api/SessionResponsesAPI";
import { useCompetenciesByIds, useAllCompetencies } from "../../api/CompetenciesAPI";
import ComparisonCinemaMap from './ComparisonCinemaMap';
import ComparisonSessionDistributionWeb from './ComparisonSessionDistributionWeb';
import ComparisonCompetencyTableWeb from './ComparisonCompetencyTableWeb';

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const styles = {
  page: {
    width: '210mm', // A4 width
    minHeight: '297mm', // A4 height
    margin: '0',
    padding: '20mm',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '16px',
    backgroundColor: 'white',
    boxShadow: '0 0 10px rgba(0,0,0,0.1)',
    marginBottom: '20px'
  },
  header: {
    marginBottom: '25px'
  },
  title: {
    fontSize: '20px',
    fontWeight: 900,
    marginBottom: '15px',
    color: '#333'
  },
  sessionDetailsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '15px'
  },
  leftDetails: {
    fontSize: '14px',
    color: '#666'
  },
  rightDetails: {
    fontSize: '14px',
    textAlign: 'right',
    color: '#666'
  },
  detailLine: {
    marginBottom: '4px'
  },
  divider: {
    borderBottom: '1px solid #e0e0e0',
    marginTop: '8px',
    marginBottom: '4px'
  },
  sectionHeader: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '20px',
    color: '#333'
  },
  comparisonSection: {
    marginBottom: '30px'
  }
};

const ComparisonReportWeb = () => {
  const [searchParams] = useSearchParams();
  
  // Get session IDs from query parameters
  const session1 = searchParams.get('session1');
  const session2 = searchParams.get('session2');
  const sessionIds = [session1, session2].filter(id => id !== null && id !== '');
  
  console.log("Comparison Report - Session IDs:", sessionIds);
  
  // Fetch session responses for both sessions
  const { 
    data: session1Responses, 
    isLoading: session1Loading 
  } = useSessionResponses(session1);
  
  const { 
    data: session2Responses, 
    isLoading: session2Loading 
  } = useSessionResponses(session2);

  // Get competency IDs from session responses
  const session1CompetencyIds = session1Responses?.map(response => response.competency_id) || [];
  const session2CompetencyIds = session2Responses?.map(response => response.competency_id) || [];
  
  // Fetch competency details using the IDs
  const { 
    data: session1Competencies, 
    isLoading: session1CompetenciesLoading 
  } = useCompetenciesByIds(session1CompetencyIds);

  const { 
    data: session2Competencies, 
    isLoading: session2CompetenciesLoading 
  } = useCompetenciesByIds(session2CompetencyIds);

  // Fetch all competencies for the distribution chart
  const { 
    data: allCompetencies, 
    isLoading: allCompetenciesLoading 
  } = useAllCompetencies();


  // Log data when it changes
  useEffect(() => {
    if (session1Responses) {
      console.log("Session 1 Responses:", session1Responses);
      console.log("Session 1 Competency IDs:", session1CompetencyIds);
    }
    if (session2Responses) {
      console.log("Session 2 Responses:", session2Responses);
      console.log("Session 2 Competency IDs:", session2CompetencyIds);
    }
  }, [session1Responses, session2Responses, session1CompetencyIds, session2CompetencyIds]);

  if (session1Loading || session2Loading || session1CompetenciesLoading || session2CompetenciesLoading || allCompetenciesLoading) {
    return (
      <div className="h-full p-6">
        <div className="flex items-center justify-center h-full">
          Loading comparison data...
        </div>
      </div>
    );
  }

  if (!session1 || !session2) {
    return (
      <div className="h-full p-6">
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Missing Session Information</h2>
            <p className="text-gray-600">Please provide both session IDs to generate comparison report.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full p-6 flex flex-col gap-6 overflow-y-auto bg-gray-100">
      <div className="w-full min-h-0 flex-1">
        <div className="space-y-6">
          <div style={styles.page}>
            <div style={styles.header}>
              <p style={styles.title}>Competency Comparison Report</p>
              
              <div style={styles.sessionDetailsContainer}>
                <div style={styles.leftDetails}>
                  <p style={styles.detailLine}>Session 1 ID: {session1}</p>
                  <p style={styles.detailLine}>Session 2 ID: {session2}</p>
                  <p style={styles.detailLine}>Total Sessions: 2</p>
                </div>
                
                <div style={styles.rightDetails}>
                  <p style={styles.detailLine}>Session 1 Competencies: {session1Competencies?.length || 0}</p>
                  <p style={styles.detailLine}>Session 2 Competencies: {session2Competencies?.length || 0}</p>
                  <p style={styles.detailLine}>Total Responses: {(session1Responses?.length || 0) + (session2Responses?.length || 0)}</p>
                </div>
              </div>
              
              <div style={styles.divider} />
            </div>
            
            <div style={styles.comparisonSection}>
              <p style={styles.sectionHeader}>The Big Picture</p>
              <ComparisonCinemaMap 
                session1Responses={session1Responses || []} 
                session2Responses={session2Responses || []} 
              />
            </div>

            <div style={styles.comparisonSection}>
              <p style={styles.sectionHeader}>The Distribution</p>
              <ComparisonSessionDistributionWeb 
                session1Responses={session1Responses || []} 
                session2Responses={session2Responses || []}
                allCompetencies={allCompetencies || []}
              />
            </div>
          </div>

          {/* The Details section with phase tables */}
          <div style={styles.page}>
            <div style={styles.header}>
              <p style={styles.sectionHeader}>The Details</p>
              <div style={styles.divider} />
            </div>
            
            {/* Phase tables */}
            {['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'].map(phase => (
              <ComparisonCompetencyTableWeb 
                key={phase}
                session1Competencies={session1Competencies || []}
                session2Competencies={session2Competencies || []}
                phase={phase}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonReportWeb;
